<template>
    <div class="container-fluid">
        <div class="row me-2">
            <div class="mt-3 fullcalendar d-flex flex-row" :class="{'loading': loading.data}">
                <FullCalendar
                    ref="calendar"
                    :options="calendarOptions"
                    class="d-flex flex-column flex-1"
                >
                    <template v-slot:eventContent='arg'>
                        <div class="d-flex flex-1 align-items-center w-100 h-100">
                            <div class="d-flex flex-1 align-items-center w-100 h-100 gap-1 event">
                                <div class="flex-fill">
                                    <tippy
                                        :ref="'eventDetails' + arg.event.id"
                                        trigger="click"
                                        placement="right"
                                        :arrow="true"
                                        :interactive="true"
                                        theme="light"
                                    >
                                        <div class="calendar-details">
                                            <img :src="arg.event.extendedProps.deck.central_image"/>
                                            <inline-svg class="calendar-details__close-btn"
                                                        src="/assets/img/icons/x-circle.svg"
                                                        @click="closeEventTooltip(arg.event.id)"
                                            ></inline-svg>
                                            <div class="calendar-details__title">{{ arg.event.title }}</div>
                                            <div class="calendar-details__category" v-if="arg.event.extendedProps.deck">{{ arg.event.extendedProps.deck.category.label }}</div>
                                            <div class="calendar-details__date">{{ arg.timeText }}   {{ arg.event.start | moment('ddd, DD MMM') }}</div>
                                            <span style="margin-bottom: 14px">Assigned by</span>
                                            <div class="row" style="margin-bottom: 24px">
                                                <user-avatar :user="arg.event.extendedProps.owner" size="xs" displayName="true"></user-avatar>
                                            </div>
                                            <div class="d-flex flex-row flex-nowrap align-items-center mb-1">
                                                <div v-if="arg.event.extendedProps.highFives.highFives.length" class="footer-text-400 flex-nowrap text-nowrap">
                                                    <inline-svg class="me-1" src="/assets/img/icons/waving-hand-small.svg"></inline-svg>
                                                    {{ getHighFiveText(arg.event.extendedProps.highFives)}}
                                                </div>
                                                <btn v-if="arg.event.extendedProps.status === 'done'"
                                                     class="action-btn ms-1 text-nowrap"
                                                     :class="{disabled: arg.event.extendedProps.highFives.myHighFive}"
                                                     @click="sendHighFive(arg.event)">
                                                    <inline-svg class="me-1" src="/assets/img/icons/waving-hand.svg"></inline-svg>
                                                    Send High Five
                                                </btn>
                                            </div>
                                            <div class="divider mb-2"></div>
                                            <div v-if="arg.event.extendedProps.canEdit" class="d-flex flex-row gap-2">
                                                <btn class="btn-outline-light white w-100"
                                                     style="flex: 1;"
                                                     @click="editTask(arg)">
                                                    Edit
                                                </btn>
                                                <a class="d-flex justify-content-center align-items-center" href="#" @click="deleteTask(arg)">
                                                    <inline-svg height="20" src="/assets/img/icons/trash-01-red.svg"></inline-svg>
                                                </a>
                                            </div>
                                        </div>
                                        <template #trigger>
                                            <div class="d-flex flex-1 align-items-center w-100 h-100 gap-1 event flex-fill">
                                                <div v-bind:style="{'background-color': arg.event.extendedProps.lineColor}" class="event__start-line-vertical"></div>
                                                <!--Status red done when the done_at date is after end date / status green done when the done_at date is before end date / no status when event was not done-->
                                                <div v-if="arg.event.extendedProps.status !== 'pending'"
                                                     class="event-status"
                                                     :class="{'success': arg.event.extendedProps.status === 'done', 'danger': arg.event.extendedProps.status === 'overdue'}"
                                                     style="color: white">
                                                    DONE
                                                </div>
                                                <div>
                                                    <div class="text-nowrap">{{ arg.event.title }}, {{ arg.timeText }}</div>
                                                    <div class="event__category" v-if="(arg.event.allDay || duration(arg) > 30) && arg.event.extendedProps.deck">{{ arg.event.extendedProps.deck.category.label }}</div>
                                                </div>
                                            </div>
                                        </template>
                                    </tippy>
                                </div>
                            </div>
                            <div class="d-flex flex-row flex-nowrap align-items-center me-1 gap-1">
                                <div v-if="arg.event.extendedProps.highFives.highFives.length" class="footer-text-400 flex-nowrap text-nowrap">
                                    <inline-svg class="me-1" src="/assets/img/icons/waving-hand-small.svg"></inline-svg>
                                    {{ getHighFiveText(arg.event.extendedProps.highFives) }}
                                </div>
                                <btn v-if="arg.event.extendedProps.status === 'done'"
                                     class="action-btn event"
                                     :class="{disabled: arg.event.extendedProps.highFives.myHighFive}"
                                     @click="sendHighFive(arg.event)">
                                    <inline-svg class="me-1" src="/assets/img/icons/waving-hand.svg"></inline-svg>
                                    High Five
                                </btn>
                            </div>
                        </div>

                    </template>
                </FullCalendar>
                <div class="col-4" style="width: 295px">
                    <calendar-picker v-model="date" @change="selectDate"/>
                </div>
            </div>
        </div>
        <modal name="addTaskModal" height="auto" width="640px" :scrollable="true">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-icon-wrapper">
                        <inline-svg src="/assets/img/icons/file-heart-01.svg"></inline-svg>
                    </div>
                    <div>
                        <h4 class="modal-title dashboard-card-title ps-2">{{newTask.id ? 'Edit' : 'Schedule an'}} Activity</h4>
                        <div class="modal-subtitle-gray ps-2">
                            Select the time, repetition and the Card Deck assigned
                        </div>
                    </div>
                    <button type="button"
                            class="btn-close"
                            data-dismiss="modal"
                            aria-hidden="true"
                            @click="$modal.hide('addTaskModal')">
                    </button>
                </div>

                <div class="divider --top-20"></div>
                <VuePerfectScrollbar style="height: 30rem">
                    <div class="modal-body">
                        <form-checkbox v-model="newTask.allDay" @changed="updateEditedFlag" text="All day"></form-checkbox>
                        <div class="row mt-2">
                            <div class="col-4 d-flex align-items-center">
                                <span>Title*</span>
                            </div>
                            <div class="col-8">
                                <form-input :v="$v.newTask.title"
                                            @changed="updateEditedFlag"
                                            placeholder="Insert the title of the activity"
                                            v-model="newTask.title"/>
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col-4 d-flex align-items-center">
                                <span>Date*</span>
                            </div>
                            <div class="col-8">
                                <date-picker prefix-class="xmx" class="w-100" v-model="newTask.date" :disabled-date="disabledStartDate">
                                </date-picker>
                            </div>
                        </div>
                        <div class="row mt-2" v-if="!newTask.allDay">
                            <div class="col-4 d-flex align-items-center">
                                <span>Time*</span>
                            </div>
                            <div class="col-8">
                                <div class="d-flex flex-row gap-1">
                                    <date-picker
                                        @change="startDateChanged"
                                        prefix-class="xmx"
                                        v-model="newTask.start"
                                        :time-picker-options="{
                                            start: '00:00',
                                            step: '00:15',
                                            end: '23:45',
                                            }"
                                        :disabled-time="disabledStartTime"
                                        format="hh:mm A"
                                        type="time"
                                        placeholder="hh:mm">
                                    </date-picker>
                                    <span class="d-flex flex-column justify-content-center">-</span>
                                    <date-picker
                                        @change="endDateChanged"
                                        prefix-class="xmx"
                                        v-model="newTask.end"
                                        :time-picker-options="{
                                            start: '00:15',
                                            step: '00:15',
                                            end: '24:00',
                                            }"
                                        :disabled-time="disabledEndTime"
                                        format="hh:mm A"
                                        type="time"
                                        placeholder="hh:mm">
                                    </date-picker>
                                </div>
                                <span v-if="checkStartEndTime" class="text-danger">*Cannot set end time before start time</span>
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col-4 d-flex align-items-center">
                                <span>Repeat</span>
                            </div>
                            <div class="col-8">
                                <form-select inputClass="form-select-lg"
                                             v-model="newTask.repeat_rule.repeat"
                                             @change="updateRepeatFlag"
                                             :options="[
                                                {'label': 'Does not repeat', 'value': null},
                                                {'label': 'Every day', 'value': 'day'},
                                                {'label': 'Every week', 'value': 'week'},
                                                {'label': 'Every month', 'value': 'month'},
                                                {'label': 'Every year', 'value': 'year'},
                                                // TODO disable for now
                                                // {'label': 'Custom', 'value': 'custom'},
                                             ]"
                                />
                            </div>
                        </div>
                        <div v-if="newTask.repeat_rule.repeat === 'custom'" class="row mt-2 gray-card">
                            <div class="col-4 d-flex align-items-center">
                                <span>Custom reoccurance</span>
                            </div>
                            <div class="col-8">
                                <div class="row">
                                    <div class="col-4 d-flex align-items-center">
                                        <span>Repeat every</span>
                                    </div>
                                    <div class="col-8" style="width: 160px">
                                        <form-select inputClass="form-select-lg"
                                                     :style="{'border-color': $v.newTask.repeat_rule.repeat_times.$invalid ? 'red' : '' }"
                                                     v-model="newTask.repeat_rule.repeat_times"
                                                     :v="$v.newTask.repeat_rule.repeat_times"
                                                     :options="[
                                                        {'label': '1 Week', 'value': 1},
                                                        {'label': '2 Weeks', 'value': 2},
                                                        {'label': '3 Weeks', 'value': 3},
                                                     ]"
                                        />
                                    </div>
                                </div>
                                <div class="row mt-2">
                                    <span class="mb-1">Repeat on</span>
                                    <repeat-day-selector :selected="newTask.repeat_rule.repeat_on"></repeat-day-selector>
                                </div>
                            </div>
                        </div>
                        <div class="row mt-2" v-if="newTask.repeat_rule.repeat">
                            <div class="col-4 d-flex align-items-center">
                                <span>Ends on*</span>
                            </div>
                            <div class="col-8">
                                <date-picker prefix-class="xmx"
                                             class="w-100"
                                             value-type="format"
                                             @change="updateRepeatFlag"
                                             :style="{'border-color': $v.newTask.repeat_rule.repeat_end.$invalid ? 'red' : '' }"
                                             v-model="newTask.repeat_rule.repeat_end"
                                             :v="$v.newTask.repeat_rule.repeat_end"
                                             :default-value="newTask.start || new Date()"
                                             :disabled-date="disabledRepeatEndDate"
                                             placeholder="Select date when repeat ends">
                                </date-picker>
                            </div>
                        </div>
                        <div class="divider --vertical-20"></div>
                        <div class="row">
                            <div class="col-4">
                                <span>Magnus Card Deck*</span>
                            </div>
                            <div class="col-8">
                                <div class="magnus-card-selector__selection" v-if="newTask.deck">
                                    <a href="#" @click="newTask.deck = null">
                                        <inline-svg src="/assets/img/icons/trash-01.svg"></inline-svg>
                                    </a>
                                    <deck-card-basic :cardDeck="newTask.deck"></deck-card-basic>
                                </div>
                                <div v-else class="magnus-card-selector" :class="{'error': $v.newTask.deck.$error}" @click="openCardDeckSelect()">
                                    <inline-svg class="mb-2" src="/assets/img/icons/upload-clip.svg"></inline-svg>
                                    <span>Click to attach <span class="magnus-card-selector__text"> a Card Deck</span></span>
                                    <div class="magnus-card-selector__text mt-1">
                                        The Card Deck attached will be sent to the Hero's MagnusCards app at the date and time selected.
                                    </div>
                                </div>
                                <span class="text-danger" v-if="$v.newTask.deck.$error">*You need to assign a Card Deck</span>
                            </div>
                        </div>
                    </div>
                </VuePerfectScrollbar>
                <div class="divider --vertical-12"></div>
                <div class="modal-footer">
                    <btn class="btn-outline-light btn-lg flex-1" @click="$modal.hide('addTaskModal')">Cancel</btn>
                    <btn class="btn-primary btn-lg flex-1"
                         :loading="loading.save"
                         :disabled="!newTask.allDay && ((!newTask.start || !newTask.end) || checkStartEndTime)"
                         @click="saveTask()">
                        {{newTask.id ? 'Save' : 'Schedule Activity'}}
                    </btn>
                </div>
            </div>
        </modal>
        <modal name="selectCardDeckModal" width="85%" height="auto" :scrollable="true" class="pt-3 pb-3">
            <div>
                <div class="modal-header">
                    <h4 class="modal-title">Select a Card Deck</h4>
                    <button type="button"
                            class="btn-close"
                            data-dismiss="modal"
                            aria-hidden="true"
                            @click="closeCardDeckSelect">
                    </button>
                </div>
                <div class="modal-body ps-4 pe-4" style="width: auto;">
                    <div class="d-flex flex-row justify-content-between">
                        <div class="d-flex flex-row">
                            <div>
                                <btn-group-toggle-with-counter :options="[
                                    {'label': 'My Card Decks', 'value': 'my_decks', 'counter': (myCardDecks ? myCardDecks.length : 0)},
                                    {'label': userNameDecksBtnName, 'value': 'user_decks', 'counter': (heroCardDecks ? heroCardDecks.length : 0)}
                                    ]" v-model="cardDecksFilter">
                                </btn-group-toggle-with-counter>
                            </div>
                        </div>
                        <div>
                            <btn class="btn btn-primary" @click="confirmCardDeckSelection()">
                                Select
                            </btn>
                        </div>
                    </div>
                    <div class="divider --vertical-24"></div>
                    <div style="overflow: auto" :class="{'loading': loading.decks}">
                        <VuePerfectScrollbar  style="height: 600px">
                            <div v-if="cardDecksFilter === 'my_decks'" class="cards-list me-2">
                                <deck-card-basic :selected="selectedCardDeck === cardDeck"
                                                 v-for="cardDeck in myCardDecks"
                                                 :cardDeck="cardDeck"
                                                 @onSelect="selectCardDeck(cardDeck)">
                                </deck-card-basic>
                            </div>
                            <div v-else class="cards-list me-2">
                                <deck-card-basic :selected="selectedCardDeck === cardDeck"
                                                 v-for="cardDeck in heroCardDecks"
                                                 :cardDeck="cardDeck"
                                                 @onSelect="selectCardDeck(cardDeck)">
                                </deck-card-basic>
                            </div>
                        </VuePerfectScrollbar>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>

import FullCalendar from '@fullcalendar/vue';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import moment from "moment";
import AvatarGroup from "@/components/AvatarGroup.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import {mapState} from "vuex";
import CalendarPicker from "@/components/CalendarPicker.vue";
import {modelListMixin} from "/src/mixins/modelListMixin.js";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import CommunityLibrary from "@/views/CommunityLibrary.vue";
import DeckCardBasic from "@/components/DeckCardBasic.vue";
import {required} from "vuelidate/lib/validators";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import RepeatDaySelector from "@/components/core/RepeatDaySelector.vue";
import BtnGroupToggle from "@/components/core/BtnGroupToggle.vue";
import BtnGroupToggleWithCounter from "@/components/core/BtnGroupToggleWithCounter.vue";

export default {
    name: 'CalendarView',
    components: {
        BtnGroupToggleWithCounter,
        BtnGroupToggle,
        RepeatDaySelector, DeckCardBasic, CommunityLibrary, CalendarPicker, UserAvatar, AvatarGroup, FullCalendar, DatePicker, VuePerfectScrollbar },
    mixins: [modelListMixin],
    computed: {
        ...mapState({
            user: state => state.user,
        }),
        checkStartEndTime() {
            return this.newTask.start > this.newTask.end;
        },
        userNameDecksBtnName() {
            return this.user.name + "'s Card Decks" ;
        }
    },
    validations: function() {
        let rules = {
            newTask: {
                title: {required: required},
                start: {required: required},
                end: {required: required},
                deck: {required: required},
                repeat_rule: {}
            }
        }
        if (this.newTask.repeat_rule.repeat) {
            rules['newTask']['repeat_rule']['repeat_end'] = { required: required }
        }
        if (this.newTask.repeat_rule.repeat === 'custom') {
            rules['newTask']['repeat_rule']['repeat_times'] = { required: required }
            rules['newTask']['repeat_rule']['repeat_on'] = { required: required }
        }

        return rules;
    },
    data() {
        return {
            loading: {
                save: false,
                data: false,
                decks: false,
            },
            date: null,
            calendarOptions: {
                plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin],
                initialView: 'timeGridDay',
                headerToolbar: {
                    left: 'title',
                    center: '',
                    right: 'prev,today,next'
                },
                height: '100%',
                allDayText: 'All Day',
                buttonText: { 'today': 'Today'},
                editable: true,
                selectable: true,
                contentHeight:"auto",
                slotDuration: '00:60',
                slotLabelInterval: '01:00',
                // slotMinTime: '08:00:00',
                slotLabelFormat: {
                    hour: 'numeric',
                    minute: '2-digit',
                    omitZeroMinute: true,
                    meridiem: 'long',
                },
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: true
                },
                displayEventTime: true,
                displayEventEnd: true,
                scrollTime: "08:00:00",
                eventDisplay: 'auto',
                events: [],
                eventClick: this.editDay,
                datesSet: this.onDatesSet,
                eventDrop: this.modifyTask,
                eventResize: this.modifyTask,
                select: (arg) => {
                    if (arg.start < new Date()) {
                        this.$dialog.alert('You cannot add activities before current date!', {
                            okText: 'Ok',
                        });
                        return;
                    }
                    this.addNewTask(arg);
                },
                prev: {
                    click: () => {
                        let calendarApi = this.$refs.fullCalendar.getApi();
                        calendarApi.prev();
                    }
                },
            },
            filters: {
                date_from: '2022-03-01',
                date_to: '2023-03-31',
            },
            cardDecksFilter: 'my_decks',
            schedule_date: null,
            schedule: null,
            newTask: {
                title: '',
                date: '',
                start: '',
                end: '',
                allDay: false,
                deck: null,
                repeat_rule: {
                    repeat: null,
                    repeat_end: null
                },
                repeatChanged: false,
                edited: false,
            },
            myCardDecks: [],
            heroCardDecks:[],
            selectedCardDeck: {},
        };
    },
    watch: {
        filters: {
            deep: true,
            handler(a) {
                this.getEvents();
            },
        },
        'newTask.date'(newDate) {
            //update start time and end time date based on the new value of date selected
            const updatedDate = moment(this.newTask.date);
            this.updateStartDate(updatedDate);

            let endDate = moment(this.newTask.end);
            if (endDate.hour() === 0) {
                endDate = updatedDate.add(1, 'd');
            }
            //always assign in this order: year, month, date
            endDate.year(updatedDate.year());
            endDate.month(updatedDate.month());
            endDate.date(updatedDate.date());
            this.newTask.end = endDate.toDate();
        },
        'newTask.repeat_rule.repeat': function(newValue, oldValue) {
            // Handle changes in the repeat property here
            this.newTask.repeat_rule.repeat_end = null;
        }
    },
    methods: {
        getEvents() {
            this.loading.data = true;
            this.$http.post("/event/list", {...this.filters, ...{
                    hero_id: this.$route.params.hero_id
                }})
                .then((res) => {
                    this.calendarOptions.events = res.body.data;
                    this.refreshEvents();
                    this.loading.data = false;
                })
                .catch((error) => {
                    this.loading.data = false;
                });
        },
        getCardDecks() {
            this.loading.decks = true;
            this.$http.post("/deck/decks-to-assign", {hero_id: this.$route.params.hero_id, filter_by: this.cardDecksFilter})
                .then((res) => {
                    this.myCardDecks = res.body.data.myCardDecks;
                    this.heroCardDecks = res.body.data.heroCardDecks;
                    this.loading.decks = false;
                })
                .catch((error) => {
                    this.loading.decks = false;
                });
        },
        disabledRepeatEndDate(date) {
            //disable dates before start for repeat end date to prevent the user to repeat in the past
            const start = moment(this.newTask.start);
            switch (this.newTask.repeat_rule.repeat) {
                case 'day':
                    return start && date < start;
                case 'week':
                    return start && date < start.add(1, 'weeks');
                case 'month':
                    return start && date < start.add(1, 'months');
                case 'year':
                    return start && date < start.add(1, 'years');
            }
        },
        disabledStartDate(date) {
            //disable dates before current date for activity start date to prevent user to add activities in the past
            return date.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
        },
        disabledStartTime(date) {
            return this.newTask.end && date > this.newTask.end;
        },
        disabledEndTime(date) {
            return this.newTask.start && date < this.newTask.start;
        },
        duration(arg) {
            let start = moment(arg.event.start);
            let end = moment(arg.event.end);
            return end.diff(start, 'minutes');
        },
        editDay(arg) {
            this.schedule_date = arg.event.title
            this.schedule = {}
            this.schedule[arg.event.title] = arg.event.extendedProps.object
            this.$modal.show('editDateScheduleModal')
        },
        onDatesSet ({ start, end }) {
            if (!moment(this.date).isSame(moment(start), 'day', null, '[]')) {
                this.date = moment(start).format('YYYY-MM-DD')
            }
            if (!moment(this.filters.date_from).isSame(moment(start), 'day', null, '[]')) {
                this.filters.date_from = start
                this.filters.date_to = end
            }
        },
        refreshEvents() {
            this.$refs.calendar.getApi().refetchEvents();
        },
        addNewTask(arg) {
            this.newTask = {
                id: null,
                title: arg.title,
                date: arg.start,
                start: arg.start,
                end: arg.end,
                allDay: arg.allDay,
                deck: null,
                repeat_rule: {
                    repeat: null,
                    repeat_times: null,
                    repeat_on: [],
                    repeat_end: null,
                }
            };
            this.$modal.show('addTaskModal');
        },
        deleteTask(arg) {
            const self = this;
            this.$refs['eventDetails' + arg.event.id].tip.hide();
            this.$dialog.confirm('Are you sure you want to delete activity?', {
                okText: 'Yes',
                cancelText: 'No',
            }).then(() => {
                self.loading.delete = true;
                self.$http.post("/event/delete", {event_id: arg.event.id })
                    .then(() => {
                        this.getEvents();
                        self.loading.delete = false;
                    })
                    .catch(() => {
                        self.loading.delete = false;
                    });
            });
        },
        editTask(arg) {
            this.$refs['eventDetails' + arg.event.id].tip.hide();
            if (arg.event.start < new Date()) {
                this.$dialog.alert('You cannot edit activities before current date!', {
                    okText: 'Ok',
                });
                return;
            }
            this.newTask = {
                id: arg.event.id,
                title: arg.event.title,
                date: arg.event.start,
                start: arg.event.start,
                end: arg.event.end,
                allDay: arg.event.allDay,
                deck: arg.event.extendedProps.deck,
                rule_id: arg.event.extendedProps.rule_id,
                repeat_rule: {
                    repeat: arg.event.extendedProps.repeat_rule.repeat ? arg.event.extendedProps.repeat_rule.repeat : null,
                    repeat_times: arg.event.extendedProps.repeat_rule.repeat_times,
                    repeat_on: arg.event.extendedProps.repeat_rule.repeat_on ? arg.event.extendedProps.repeat_rule.repeat_on : [],
                    repeat_end: arg.event.extendedProps.repeat_rule.repeat_end
                },
                repeatChanged: false,
                edited: false,
            };
            this.$modal.show('addTaskModal');
        },
        selectDate (date, showDay = false) {
            this.date = date
            this.newTask.date = date;
            this.$refs.calendar.getApi().gotoDate(date)
        },
        async saveTask() {
            this.$v.newTask.$touch();
            if (this.$v.newTask.$invalid == true) {
                return false;
            }
            this.newTask.hero_id = this.$route.params.hero_id;
            if (this.newTask.id && this.newTask.rule_id && this.newTask.repeat_rule.repeat) {
                if (this.newTask.edited) {
                    await this.$dialog
                        .confirm('Do you want to edit', {
                            okText: 'This and following events',
                            cancelText: 'This event',
                        })
                        .then(() => {
                            this.newTask.editFollowingEvents = true;
                        })
                        .catch(() => {
                        });
                }
            }
            this.loading.save = true;
            const url = this.newTask.id ? "/event/update-event" : "/event/create-event"
            this.$http.post(url, this.newTask)
                .then((res) => {
                    this.$modal.hide('addTaskModal');
                    this.getEvents();
                    this.$events.emit('loadHeroCardDecks');
                    this.loading.save = false;
                })
                .catch((error) => {
                    this.loading.save = false;
                });
        },
        openCardDeckSelect() {
            this.$modal.show('selectCardDeckModal');
            this.getCardDecks();
        },
        closeCardDeckSelect() {
            this.selectedCardDeck = null;
            this.$modal.hide('selectCardDeckModal')
        },
        selectCardDeck(cardDeck) {
            this.selectedCardDeck = cardDeck;
        },
        confirmCardDeckSelection() {
            this.newTask.deck = this.selectedCardDeck;
            this.$modal.hide('selectCardDeckModal');
        },
        closeEventTooltip(event_id) {
            if (this.$refs['eventDetails' + event_id]) {
                this.$refs['eventDetails' + event_id].tip.hide()
            }
        },
        modifyTask(arg) {
            if (arg.event.start < new Date()) {
                this.$dialog.alert('You cannot edit activities before current date!', {
                    okText: 'Ok',
                });
                return;
            }
            this.newTask = {
                id: arg.event.id,
                title: arg.event.title,
                date: arg.event.start,
                start: arg.event.start,
                end: arg.event.end,
                allDay: arg.event.allDay,
                deck: arg.event.extendedProps.deck,
                rule_id: arg.event.extendedProps.rule_id,
                repeat_rule: {
                    repeat: arg.event.extendedProps.repeat_rule.repeat ? arg.event.extendedProps.repeat_rule.repeat : null,
                    repeat_times: arg.event.extendedProps.repeat_rule.repeat_times,
                    repeat_on: arg.event.extendedProps.repeat_rule.repeat_on ? arg.event.extendedProps.repeat_rule.repeat_on : [],
                    repeat_end: arg.event.extendedProps.repeat_rule.repeat_end
                },
                repeatChanged: false,
                edited: true,
            };
            this.saveTask();
        },
        sendHighFive(event) {
            if (event.extendedProps.highFives.myHighFive) {
                return;
            }
            this.loading.save = true;
            this.$http.post("/reaction/save", {event_id: event.id, type: 'high_five'})
                .then((res) => {
                    this.getEvents();
                    this.$events.emit('loadMilestones');
                    this.loading.save = false;
                })
                .catch((error) => {
                    this.loading.save = false;
                });
        },
        getHighFiveText(data) {
            if (!data.highFives.length) {
                return;
            }
            let message = '';
            if (data.myHighFive) {
                message = 'You ';
            } else {
                message = data.highFives[0].caregiver;
            }
            if (data.highFives.length > 1) {
                message += '& ' + (data.highFives.length - 1) + ' others';
            }
            return message;
        },
        updateRepeatFlag() {
            this.newTask.repeatChanged = true;
        },
        updateEditedFlag() {
            this.newTask.edited = true;
        },
        updateStartDate(updatedDate) {
            let startDate = moment(this.newTask.start);
            //always assign in this order: year, month, date
            startDate.year(updatedDate.year());
            startDate.month(updatedDate.month());
            startDate.date(updatedDate.date());
            this.newTask.start = startDate.toDate();
        },
        startDateChanged() {
            this.updateStartDate(moment(this.newTask.date));
            this.updateEditedFlag();
        },
        endDateChanged() {
            const taskDate = moment(this.newTask.date);
            let endDate = moment(this.newTask.end);
            if ((endDate.hour() !== 0 || endDate.minutes() !== 0) && endDate.format('YYYY-MM-DD') !== taskDate.format('YYYY-MM-DD')) {
                //always assign in this order: year, month, date
                endDate.year(taskDate.year());
                endDate.month(taskDate.month());
                endDate.date(taskDate.date());
                this.newTask.end = endDate.toDate();
            }
            this.updateEditedFlag();
        },
    }
};
</script>
